import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { SettingsProvider } from "./context/SettingsContext";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import AppShell from "./components/AppShell";
import Attendance from "./pages/Attendance";
import Monthly from "./pages/Monthly";
import Absentees from "./pages/Absentees";
import AuditLog from "./pages/AuditLog";
import StudentDetail from "./pages/StudentDetail";
import Groups from "./pages/Groups";
import Admins from "./pages/Admins";
import SettingsPage from "./pages/Settings";
import NewYear from "./pages/NewYear";
import WaitingApproval from "./components/WaitingApproval";
import { isSuper, isManager, isPending } from "./lib/roles";

function Protected({ children, superOnly, managerOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (isPending(user)) return <WaitingApproval />;
  if (superOnly && !isSuper(user)) return <Navigate to="/" replace />;
  if (managerOnly && !isManager(user)) return <Navigate to="/" replace />;
  return children;
}

function RoutedApp() {
  const location = useLocation();
  // Process the OAuth redirect (session_id in URL fragment) before any route/auth check.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/" element={<Attendance />} />
        <Route path="/monthly" element={<Monthly />} />
        <Route path="/absentees" element={<Absentees />} />
        <Route path="/student/:id" element={<StudentDetail />} />
        <Route path="/groups" element={<Protected managerOnly><Groups /></Protected>} />
        <Route path="/admins" element={<Protected managerOnly><Admins /></Protected>} />
        <Route path="/new-year" element={<Protected managerOnly><NewYear /></Protected>} />
        <Route path="/audit" element={<Protected superOnly><AuditLog /></Protected>} />
        <Route path="/settings" element={<Protected superOnly><SettingsPage /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <SettingsProvider>
          <AuthProvider>
            <BrowserRouter>
              <RoutedApp />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </SettingsProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
